import { useEffect, useState, type FormEvent } from 'react';
import {
  Button,
  Input,
  Card,
  FormField,
  NotificationCard,
  UniversalLoadingState,
  useToast,
} from '@sighthost/ui';
import { supabase } from '@/shared/lib/supabase';

/**
 * SettingsProfilePage (S3.1) — name + phone.
 *
 * The full mailing address + invoice email lives in SettingsBillingInfoPage.
 * This page covers the minimal "who are you" identity fields stored on
 * customer_profile (RLS-scoped to auth.uid()). Email is read-only — changing
 * the sign-in email goes through support per existing customer_profile
 * trigger constraints.
 */

interface ProfileRow {
  id: string;
  full_name: string | null;
  phone: string | null;
}

export default function SettingsProfilePage() {
  const { toast } = useToast();
  const [email, setEmail] = useState<string>('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not signed in.');
        if (cancelled) return;
        setEmail(user.email ?? '');

        const { data: profile, error: profErr } = await supabase
          .from('customer_profile')
          .select('id, full_name, phone')
          .eq('id', user.id)
          .maybeSingle();
        if (profErr) throw new Error(profErr.message);
        if (cancelled) return;

        const p = profile as ProfileRow | null;
        setFullName(p?.full_name ?? (user.user_metadata?.full_name as string | undefined) ?? '');
        setPhone(p?.phone ?? '');
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not signed in.');

      // UPDATE, not upsert. An upsert is INSERT ... ON CONFLICT DO UPDATE, and
      // Postgres evaluates the INSERT policy even when the row already exists.
      // customer_profile's only INSERT policy (customer_profile_lead_insert)
      // requires sales_manager or admin, so a customer saving their own details
      // failed with:
      //   42501: new row violates row-level security policy for "customer_profile"
      // The UPDATE policy (customer_profile_update_own, id = auth.uid()) was
      // correct all along — the statement was simply the wrong one. The row is
      // always created by the handle_new_user trigger at signup, so there is
      // nothing to insert.
      const { data: updated, error: upErr } = await supabase
        .from('customer_profile')
        .update({
          full_name: fullName.trim() || null,
          phone: phone.trim() || null,
        })
        .eq('id', user.id)
        .select('id');
      if (upErr) throw new Error(upErr.message);
      // RLS filters rather than errors on a non-matching UPDATE, so zero rows
      // means "silently saved nothing" — surface that instead of a false success.
      if (!updated || updated.length === 0) {
        throw new Error('Your profile could not be updated. Please contact support.');
      }

      toast({ tone: 'success', title: 'Profile saved' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not save.';
      setError(msg);
      toast({ tone: 'danger', title: 'Save failed', body: msg });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card interactive={false} className="p-12">
        <UniversalLoadingState variant="spinner" caption="Loading profile…" />
      </Card>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-ink">Identity</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Your name and phone are used on invoices and on the WHOIS record for any domains
            you register through the portal.
          </p>
        </header>
        <div className="space-y-4 px-6 py-5">
          <FormField label="Full name" htmlFor="full_name" required>
            <Input
              id="full_name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              disabled={saving}
            />
          </FormField>
          <FormField label="Email" htmlFor="email" helper="Linked to your sign-in account. Contact support to change.">
            <Input id="email" value={email} disabled readOnly />
          </FormField>
          <FormField label="Phone" htmlFor="phone" helper="Format: +27 76 700 4882">
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              placeholder="+27 76 700 4882"
              disabled={saving}
            />
          </FormField>
        </div>
      </Card>

      {error && <NotificationCard tone="danger" title="Save failed" body={error} />}

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} loading={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}
